// Extracted from invoices/index.tsx to keep the grandfathered route under its
// existing line count when adopting the RouteShell wrapper.

import { X } from "lucide-react";
import { bulkActionLabel, draftActionTooltip, pushActionTooltip } from "./-helpers";

type BulkActionBarProps = {
	selectedCount: number;
	draftCount: number;
	pushableCount: number;
	canApproveBulk: boolean;
	canSkipBulk: boolean;
	canDeleteBulk: boolean;
	canPushBulk: boolean;
	bulkPending: boolean;
	qboConnected: boolean;
	deletePending: boolean;
	skipPending: boolean;
	approvePending: boolean;
	pushPending: boolean;
	onClear: () => void;
	onDelete: () => void;
	onSkip: () => void;
	onApprove: () => void;
	onPush: () => void;
};

export function BulkActionBar(p: BulkActionBarProps) {
	if (p.selectedCount <= 0) return null;
	return (
		<div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#4f46e5]/30 bg-[#eef2ff] px-4 py-2.5">
			<span className="text-sm font-medium text-[#4f46e5]">{p.selectedCount} selected</span>
			<button
				type="button"
				onClick={p.onClear}
				className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs text-[#4f46e5] hover:bg-white/60"
			>
				<X className="h-3 w-3" strokeWidth={1.5} /> Clear
			</button>
			<div className="ml-auto flex flex-wrap items-center gap-2">
				<button
					type="button"
					onClick={p.onDelete}
					disabled={!p.canDeleteBulk || p.bulkPending}
					title={draftActionTooltip(p.draftCount, p.selectedCount)}
					className="h-8 rounded border border-[#e5e7eb] bg-white px-3 text-sm font-medium text-[#6b7280] transition-colors hover:bg-[#f8f9fb] disabled:cursor-not-allowed disabled:opacity-40"
				>
					{p.deletePending
						? "Deleting..."
						: bulkActionLabel("Delete", p.draftCount, p.selectedCount, "draft")}
				</button>
				<button
					type="button"
					onClick={p.onSkip}
					disabled={!p.canSkipBulk || p.bulkPending}
					title={draftActionTooltip(p.draftCount, p.selectedCount)}
					className="h-8 rounded border border-[#d1d5db] bg-white px-3 text-sm font-medium text-[#6b7280] transition-colors hover:bg-[#f8f9fb] disabled:cursor-not-allowed disabled:opacity-40"
				>
					{p.skipPending
						? "Saving..."
						: bulkActionLabel("Already Billed", p.draftCount, p.selectedCount, "draft")}
				</button>
				<button
					type="button"
					onClick={p.onApprove}
					disabled={!p.canApproveBulk || p.bulkPending}
					title={draftActionTooltip(p.draftCount, p.selectedCount)}
					className="h-8 rounded bg-[#16a34a] px-3 text-sm font-semibold text-white transition-colors hover:bg-[#15803d] disabled:cursor-not-allowed disabled:opacity-40"
				>
					{p.approvePending
						? "Approving..."
						: bulkActionLabel("Approve", p.draftCount, p.selectedCount, "draft")}
				</button>
				{p.qboConnected && (
					<button
						type="button"
						onClick={p.onPush}
						disabled={!p.canPushBulk || p.bulkPending}
						title={pushActionTooltip(p.pushableCount, p.selectedCount)}
						className="h-8 rounded bg-[#4f46e5] px-3 text-sm font-semibold text-white transition-colors hover:bg-[#4338ca] disabled:cursor-not-allowed disabled:opacity-40"
					>
						{p.pushPending
							? "Pushing..."
							: bulkActionLabel("Push to QuickBooks", p.pushableCount, p.selectedCount, "approved")}
					</button>
				)}
			</div>
		</div>
	);
}
